import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import campaignApi from "../api/campaignApi";
import ModulePageShell from "../components/common/ModulePageShell";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import useGoalCycleOptions from "../hooks/useGoalCycleOptions";
import { buildReadableCode } from "../utils/performanceCode";

const CampaignsPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { goalCycleOptions, isLoading: goalCyclesLoading } =
    useGoalCycleOptions();
  const [showGuide, setShowGuide] = React.useState(false);
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const canRead360 = can(PERFORMANCE_PERMISSIONS.APPRAISAL_360_READ);
  const canManage360 = can(PERFORMANCE_PERMISSIONS.APPRAISAL_360_MANAGE);

  if (!canRead360) {
    return (
      <ModulePageShell
        title="360 Campaigns"
        description="Create and manage 360 review campaigns and participants."
      >
        <EmptyState message="You do not have permission to view 360 campaigns." />
      </ModulePageShell>
    );
  }

  const listQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.campaigns],
    queryFn: () => campaignApi.listCampaigns({ page: 1, limit: 50 }),
  });

  const createMutation = useMutation({
    mutationFn: campaignApi.createCampaign,
    onSuccess: () => {
      toast({ title: "Campaign created", status: "success", duration: 2000 });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.campaigns],
      });
      reset();
    },
    onError: (error) => {
      toast({
        title: "Failed to create campaign",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      name: "",
      goalCycleId: "",
      startDate: "",
      endDate: "",
      status: "DRAFT",
    },
  });

  const rows = listQuery.data?.data || [];

  return (
    <ModulePageShell
      title="360 Campaigns"
      description="Create and manage 360 review campaigns and participants."
      onRefresh={() => listQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {canManage360 ? (
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
        >
          <form
            onSubmit={handleSubmit((values) =>
              createMutation.mutate({
                ...values,
                code: buildReadableCode("C360", values.name),
              }),
            )}
          >
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="semibold">Create Campaign</Text>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Campaign Name</FormLabel>
                  <Input {...register("name")} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Goal Cycle</FormLabel>
                  <Select
                    placeholder="Select goal cycle"
                    {...register("goalCycleId")}
                    isDisabled={goalCyclesLoading}
                  >
                    {goalCycleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Start Date</FormLabel>
                  <Input type="date" {...register("startDate")} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>End Date</FormLabel>
                  <Input type="date" {...register("endDate")} />
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select {...register("status")}>
                    <option value="DRAFT">DRAFT</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="CLOSED">CLOSED</option>
                  </Select>
                </FormControl>
              </HStack>
              <Button
                type="submit"
                colorScheme="blue"
                alignSelf="flex-start"
                isLoading={createMutation.isPending}
              >
                Create Campaign
              </Button>
            </VStack>
          </form>
        </Box>
      ) : (
        <EmptyState message="You have read access to campaigns, but cannot create campaigns." />
      )}

      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={4}
      >
        {listQuery.isLoading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <EmptyState message="No campaigns available." />
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Campaign Name</Th>
                <Th>Cycle</Th>
                <Th>Start Date</Th>
                <Th>End Date</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((item) => (
                <Tr key={item._id}>
                  <Td>{item.name}</Td>
                  <Td>{item.goalCycleId?.name || item.goalCycleId || "-"}</Td>
                  <Td>
                    {item.startDate
                      ? new Date(item.startDate).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>
                    {item.endDate
                      ? new Date(item.endDate).toLocaleDateString()
                      : "-"}
                  </Td>
                  <Td>{item.status}</Td>
                  <Td>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        navigate(`/performance/360/campaigns/${item._id}`)
                      }
                    >
                      View
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </ModulePageShell>
  );
};

export default CampaignsPage;
